<template>
  <div class="page-layout">
    <div class="page-content">
      <h1>Мой профиль</h1>
      <form class="panel track-form" @submit.prevent="submit">
        <div class="form-field">
          <label>Аватар</label>
          <div class="avatar-field">
            <UserAvatar :user="previewUser" size="lg" />
            <div class="avatar-actions">
              <input
                ref="avatarInput"
                id="profile-avatar"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                class="avatar-input"
                @change="onAvatarSelected"
              />
              <label for="profile-avatar" class="btn btn-secondary avatar-upload-label">
                Выбрать файл
              </label>
              <button
                v-if="hasAvatar"
                type="button"
                class="btn btn-secondary"
                @click="removeAvatar"
              >
                Удалить аватар
              </button>
              <p class="field-hint">JPEG/PNG/WebP, до 25 МБ. Превью показывается сразу после выбора.</p>
            </div>
          </div>
        </div>

        <div class="form-field">
          <label for="profile-email">Email</label>
          <input id="profile-email" :value="email" type="email" disabled />
        </div>

        <div class="form-field">
          <label for="profile-name">Имя</label>
          <input
            id="profile-name"
            v-model="name"
            type="text"
            maxlength="100"
            placeholder="Как вас зовут"
          />
        </div>

        <div class="form-field">
          <label for="profile-username">Username</label>
          <input
            id="profile-username"
            v-model="username"
            type="text"
            maxlength="30"
            placeholder="Иван Петров"
            autocapitalize="off"
            autocomplete="username"
          />
          <p class="field-hint">Можно использовать кириллицу и пробелы. Пробелы при сохранении заменяются на _.</p>
        </div>

        <div class="form-field">
          <label for="profile-city">Город</label>
          <CityPicker input-id="profile-city" v-model="city" />
          <p class="field-hint">Начальная точка на карте</p>
        </div>

        <div class="form-field">
          <label for="profile-about">Обо мне</label>
          <RichTextEditor
            id="profile-about"
            v-model="about"
            :allow-images="false"
            placeholder="Расскажите о себе..."
          />
        </div>

        <div class="form-field">
          <label class="checkbox-label">
            <input
              v-model="profilePublic"
              type="checkbox"
              :disabled="!username.trim()"
            />
            Сделать профиль публичным
          </label>
          <p v-if="!username.trim()" class="field-hint">
            Укажите username, чтобы открыть профиль для просмотра.
          </p>
        </div>

        <div v-if="profilePublic && username.trim()" class="share-links panel">
          <h2>Ссылки для sharing</h2>
          <ShareLink label="Профиль" :url="profileUrl" />
          <ShareLink label="Публичные треки" :url="tracksUrl" />
          <ShareLink label="Публичные поездки" :url="tripsUrl" />
        </div>

        <p v-if="error" class="error-message">{{ error }}</p>
        <p v-if="success" class="success-message">Профиль сохранён</p>

        <button class="btn" type="submit" :disabled="loading">
          {{ loading ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </form>

      <section class="vehicles-section panel">
        <div class="vehicles-section-header">
          <h2>Мои автомобили</h2>
          <button type="button" class="btn btn-secondary" @click="startCreateVehicle">
            Добавить автомобиль
          </button>
        </div>

        <p v-if="vehiclesLoading" class="empty-state">Загрузка...</p>
        <p v-else-if="vehiclesError" class="error-message">{{ vehiclesError }}</p>
        <p v-else-if="!vehicles.length && !editingVehicle" class="empty-state">
          Пока нет сохранённых автомобилей
        </p>

        <ul v-else-if="!editingVehicle" class="vehicles-list">
          <li v-for="vehicle in vehicles" :key="vehicle.id">
            <VehicleCard :vehicle="vehicle" show-actions>
              <template #actions>
                <button type="button" class="btn btn-secondary" @click="startEditVehicle(vehicle)">
                  Редактировать
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="deletingVehicleId === vehicle.id"
                  @click="removeVehicle(vehicle)"
                >
                  Удалить
                </button>
              </template>
            </VehicleCard>
          </li>
        </ul>

        <form v-if="editingVehicle" class="vehicle-editor" @submit.prevent="saveVehicle">
          <h3>{{ editingVehicle.id ? 'Редактирование' : 'Новый автомобиль' }}</h3>
          <VehicleForm
            v-model:make="vehicleMake"
            v-model:equipment="vehicleEquipment"
            v-model:photos="vehiclePhotos"
            :disabled="vehicleSaving"
          />
          <p v-if="vehicleFormError" class="error-message">{{ vehicleFormError }}</p>
          <div class="vehicle-editor-actions">
            <button class="btn" type="submit" :disabled="vehicleSaving">
              {{ vehicleSaving ? 'Сохранение...' : 'Сохранить' }}
            </button>
            <button type="button" class="btn btn-secondary" @click="cancelVehicleEdit">
              Отмена
            </button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import CityPicker from '../components/CityPicker.vue'
import RichTextEditor from '../components/RichTextEditor.vue'
import ShareLink from '../components/ShareLink.vue'
import UserAvatar from '../components/UserAvatar.vue'
import VehicleCard from '../components/VehicleCard.vue'
import VehicleForm from '../components/VehicleForm.vue'
import { createVehicle, deleteVehicle, fetchVehicles, updateVehicle } from '../api/vehicles'
import { useAuthStore } from '../stores/auth'
import {
  publicProfileTracksUrl,
  publicProfileTripsUrl,
  publicProfileUrl,
} from '../utils/publicProfileUrls'

const auth = useAuthStore()
const MAX_AVATAR_SIZE_BYTES = 25 * 1024 * 1024

const email = ref('')
const name = ref('')
const username = ref('')
const city = ref(null)
const about = ref('')
const profilePublic = ref(false)
const avatarFile = ref(null)
const removeAvatarFlag = ref(false)
const avatarPreviewUrl = ref('')
const avatarInput = ref(null)
const loading = ref(false)
const error = ref('')
const success = ref(false)

const vehicles = ref([])
const vehiclesLoading = ref(false)
const vehiclesError = ref('')
const editingVehicle = ref(null)
const vehicleMake = ref('')
const vehicleEquipment = ref('')
const vehiclePhotos = ref([])
const vehicleSaving = ref(false)
const vehicleFormError = ref('')
const deletingVehicleId = ref(null)

const previewUser = computed(() => ({
  id: auth.user?.id,
  name: name.value,
  username: username.value,
  avatar_url: avatarPreviewUrl.value || (removeAvatarFlag.value ? null : auth.user?.avatar_url),
}))

const hasAvatar = computed(() => Boolean(previewUser.value.avatar_url))

const profileUrl = computed(() => publicProfileUrl(username.value.trim()))
const tracksUrl = computed(() => publicProfileTracksUrl(username.value.trim()))
const tripsUrl = computed(() => publicProfileTripsUrl(username.value.trim()))

function fillForm(user) {
  email.value = user?.email ?? ''
  name.value = user?.name ?? ''
  username.value = user?.username ?? ''
  profilePublic.value = Boolean(user?.profile_public)
  city.value =
    user?.city && user?.city_lat != null && user?.city_lng != null
      ? { city: user.city, lat: user.city_lat, lng: user.city_lng }
      : null
  about.value = user?.about ?? ''
}

function clearAvatarSelection() {
  avatarFile.value = null
  removeAvatarFlag.value = false
  if (avatarPreviewUrl.value) {
    URL.revokeObjectURL(avatarPreviewUrl.value)
    avatarPreviewUrl.value = ''
  }
  if (avatarInput.value) {
    avatarInput.value.value = ''
  }
}

function onAvatarSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return
  if (file.size > MAX_AVATAR_SIZE_BYTES) {
    error.value = 'Аватар должен быть не больше 25 МБ'
    if (avatarInput.value) {
      avatarInput.value.value = ''
    }
    return
  }

  if (avatarPreviewUrl.value) {
    URL.revokeObjectURL(avatarPreviewUrl.value)
  }

  error.value = ''
  avatarFile.value = file
  removeAvatarFlag.value = false
  avatarPreviewUrl.value = URL.createObjectURL(file)
}

function removeAvatar() {
  avatarFile.value = null
  removeAvatarFlag.value = true
  if (avatarPreviewUrl.value) {
    URL.revokeObjectURL(avatarPreviewUrl.value)
    avatarPreviewUrl.value = ''
  }
  if (avatarInput.value) {
    avatarInput.value.value = ''
  }
}

async function loadVehicles() {
  vehiclesLoading.value = true
  vehiclesError.value = ''
  try {
    const { data } = await fetchVehicles()
    vehicles.value = data
  } catch {
    vehiclesError.value = 'Не удалось загрузить автомобили'
  } finally {
    vehiclesLoading.value = false
  }
}

function startCreateVehicle() {
  editingVehicle.value = {}
  vehicleMake.value = ''
  vehicleEquipment.value = ''
  vehiclePhotos.value = []
  vehicleFormError.value = ''
}

function startEditVehicle(vehicle) {
  editingVehicle.value = vehicle
  vehicleMake.value = vehicle.make
  vehicleEquipment.value = vehicle.equipment || ''
  vehiclePhotos.value = []
  vehicleFormError.value = ''
}

function cancelVehicleEdit() {
  editingVehicle.value = null
  vehicleMake.value = ''
  vehicleEquipment.value = ''
  vehiclePhotos.value = []
  vehicleFormError.value = ''
}

async function saveVehicle() {
  vehicleSaving.value = true
  vehicleFormError.value = ''
  try {
    const payload = { make: vehicleMake.value, equipment: vehicleEquipment.value }
    if (editingVehicle.value?.id) {
      const { data } = await updateVehicle(editingVehicle.value.id, payload, vehiclePhotos.value)
      vehicles.value = vehicles.value.map((item) => (item.id === data.id ? data : item))
    } else {
      const { data } = await createVehicle(payload, vehiclePhotos.value)
      vehicles.value = [data, ...vehicles.value]
    }
    cancelVehicleEdit()
  } catch (err) {
    vehicleFormError.value =
      err.response?.data?.errors?.join(', ') || 'Не удалось сохранить автомобиль'
  } finally {
    vehicleSaving.value = false
  }
}

async function removeVehicle(vehicle) {
  deletingVehicleId.value = vehicle.id
  vehiclesError.value = ''
  try {
    await deleteVehicle(vehicle.id)
    vehicles.value = vehicles.value.filter((item) => item.id !== vehicle.id)
  } catch (err) {
    vehiclesError.value =
      err.response?.data?.errors?.join(', ') || 'Не удалось удалить автомобиль'
  } finally {
    deletingVehicleId.value = null
  }
}

async function submit() {
  loading.value = true
  error.value = ''
  success.value = false

  try {
    await auth.updateProfile(name.value, username.value, {
      avatar: avatarFile.value,
      removeAvatar: removeAvatarFlag.value,
      profilePublic: profilePublic.value,
      city: city.value?.city ?? '',
      cityLat: city.value?.lat ?? null,
      cityLng: city.value?.lng ?? null,
      about: about.value,
    })
    fillForm(auth.user)
    clearAvatarSelection()
    success.value = true
  } catch (err) {
    const errors = err.response?.data?.errors
    error.value = Array.isArray(errors) ? errors.join(', ') : 'Не удалось сохранить профиль'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fillForm(auth.user)
  loadVehicles()
})

onUnmounted(() => {
  if (avatarPreviewUrl.value) {
    URL.revokeObjectURL(avatarPreviewUrl.value)
  }
})
</script>

<style scoped>
.avatar-field {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.avatar-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.avatar-input {
  display: none;
}

.avatar-upload-label {
  cursor: pointer;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.field-hint {
  margin: 0.5rem 0 0;
  color: #64748b;
  font-size: 0.875rem;
}

.share-links {
  margin: 1rem 0;
  padding: 1rem;
  background: #f8fafc;
}

.share-links h2 {
  margin: 0 0 0.75rem;
  font-size: 1rem;
}

.success-message {
  color: var(--color-success, #2e7d32);
  margin: 0 0 1rem;
}

.vehicles-section {
  margin-top: 1.5rem;
}

.vehicles-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.vehicles-section-header h2 {
  margin: 0;
  font-size: 1.125rem;
}

.vehicles-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.vehicle-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.vehicle-editor h3 {
  margin: 0;
  font-size: 1rem;
}

.vehicle-editor-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
</style>
